import airbrake from 'helpers/airbrake';
import { buildGrantUrl } from 'components/nylas/v3/grant/helpers';
import { get, post } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { stripQueryParameter } from 'helpers/browser_helpers';
import { scopesFor } from 'components/nylas/v3/grant/scopes';

import type { ProviderName } from 'components/nylas/v3/types';
import type {
  ApiError, EmailDraft, EmailFile, EmailFileReference, PendingReply, ReplyMode, SendEmailConfig
} from './types';

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export const MAILBOX_LABELS: Record<ProviderName, string> = { google: 'Gmail', microsoft: 'Outlook' };

interface EmailPayload {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
}

export function splitEmails(value: string): string[] {
  return value.split(',').map(email => email.trim()).filter(Boolean);
}

export function errorMessage(error: ApiError): string {
  const { status, data } = error.response ?? {};
  const code = data?.error;

  if (status === 403 && code === 'reauthentication_required') {
    return 'Your email connection needs to be reconnected before you can send. ' +
      'Reconnect it from your integration settings and try again.';
  }
  if (code === 'duplicate_send') {
    return 'This email has already been sent.';
  }
  if (code === 'not_enabled') {
    return 'Sending email is not enabled for your account.';
  }

  return code || 'Something went wrong sending your email. Please try again.';
}

export function buildRequestBody(
  { to, cc, bcc, subject, body }: EmailPayload,
  files: EmailFile[],
  idempotencyKey: string
): FormData | object {
  if (files.length === 0) {
    return { to, cc, bcc, subject, body, idempotencyKey };
  }

  const data = new FormData();
  to.forEach(email => data.append('to[]', email));
  cc.forEach(email => data.append('cc[]', email));
  bcc.forEach(email => data.append('bcc[]', email));
  data.append('subject', subject);
  data.append('body', body);
  data.append('idempotency_key', idempotencyKey);
  files.forEach((file) => {
    if (isFileReference(file)) {
      data.append('ai_attachment_gids[]', file.gid);
    } else {
      data.append('attachments[]', file);
    }
  });
  return data;
}

export function isFileReference(file: EmailFile): file is EmailFileReference {
  return !(file instanceof File);
}

export function getFileName(file: EmailFile): string {
  return isFileReference(file) ? file.filename : file.name;
}

export function getFileSize(file: EmailFile): number {
  return isFileReference(file) ? file.byteSize : file.size;
}

// Round-tripped through Nylas as the OAuth `state` so the callback can return the broker to this
// contact and reopen the compose modal. Parsed server-side against /\Acompose_email:(\d+)\z/.
export function composeReturnState(contactId: number): string {
  return `compose_email:${contactId}`;
}

export function buildReauthUrl(authPath: string, provider: ProviderName, contactId: number, withSend = true): URL {
  return buildGrantUrl(authPath, provider, scopesFor(provider, withSend), composeReturnState(contactId));
}

const PENDING_REPLY_KEY = 'apex.activities.pendingReply';

export function storePendingReply(activityId: number, mode: ReplyMode, contactId: number): void {
  try {
    window.sessionStorage.setItem(PENDING_REPLY_KEY, JSON.stringify({ activityId, mode, contactId }));
  } catch {
    // Private browsing or a full quota - the broker still lands on the contact, just unprefilled.
  }
}

function readPendingReply(): PendingReply | null {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PENDING_REPLY_KEY) ?? 'null');
    if (typeof parsed?.activityId !== 'number' || typeof parsed?.contactId !== 'number') return null;

    return {
      activityId: parsed.activityId,
      mode: parsed.mode === 'replyAll' ? 'replyAll' : 'reply',
      contactId: parsed.contactId
    };
  } catch {
    return null;
  }
}

export function hasPendingReply(contactId?: number): boolean {
  const pending = readPendingReply();
  return pending !== null && pending.contactId === contactId;
}

export function clearPendingReply(): void {
  try {
    window.sessionStorage.removeItem(PENDING_REPLY_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
}

export function takePendingReply(search: string, sendEmail?: SendEmailConfig): PendingReply | null {
  if (!fromCompose(search)) return null;

  const pending = readPendingReply();
  clearPendingReply();
  if (!pending || !shouldAutoOpenCompose(search, sendEmail) || pending.contactId !== sendEmail?.contactId) return null;

  return pending;
}

export function fromCompose(search: string): boolean {
  return new URLSearchParams(search).get('compose_email') === '1';
}

// True when the page was reached via the post-reauth redirect and the mailbox can now send.
export function shouldAutoOpenCompose(search: string, sendEmail?: SendEmailConfig): boolean {
  if (!sendEmail?.enabled || !sendEmail.canSend || !sendEmail.path) return false;

  return fromCompose(search);
}

export async function takeEmailDraft(sendEmail?: SendEmailConfig): Promise<EmailDraft | null> {
  const actionId = stripQueryParameter('ai_action_id');
  if (!sendEmail?.enabled || !sendEmail.canSend || !sendEmail.path || !actionId) {
    return null;
  }

  try {
    const { attachments, payload, rejectPath } = await get(`/ai/actions/${actionId}`);
    // The draft belongs to this form now. No request_id: a form opening is nothing for the model to
    // answer, and a rejection that didn't take is not the broker's problem mid-compose.
    post(rejectPath, { reason: 'Edited' }).catch(error => airbrake.notify(error));

    return { attachments, body: payload.body, subject: payload.subject };
  } catch {
    showSnackbar({ text: "That draft couldn't be opened.", variant: 'danger' });
    return null;
  }
}
